// src/pages/Agents/RouterAgentModal.js
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from "../../utils/axiosInstance";
import { hasPermission } from "../../utils/permissionUtils";
import { useParams } from 'react-router-dom';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, CircularProgress, Alert } from '@mui/material';

// Default prompts for the router agent
const DEFAULT_ROUTER_GOAL = `Analyze the user's query and classify its primary intent. Respond with 'DATABASE' for data-related questions, 'CALENDAR' for scheduling or event management, or 'GENERAL' for other types of requests.`;
const DEFAULT_ROUTER_BACKSTORY = `You are an intelligent request routing system. Your job is to quickly understand what the user wants and direct their query to the correct specialized capability.`;
const DEFAULT_ROUTER_DESCRIPTION = `Analyze the following user query: '{user_query}'.
Based on the query, classify the user's intent.
If the query is about fetching data, information from a database, products, stock, prices, etc., classify it as 'DATABASE'.
If the query is about booking appointments, scheduling meetings, managing calendar events, etc., classify it as 'CALENDAR'.
For any other type of query, classify it as 'GENERAL'.
Your final answer MUST be a single word: DATABASE, CALENDAR, or GENERAL.`;

export default function RouterAgentModal({ open, onClose }) {
    const [formData, setFormData] = useState({ 
        goal: '', 
        backstory: '', 
        task_description: '' 
    });
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [loading, setLoading] = useState(true);
    const { chatbotId: paramsChatbotId } = useParams();

    // Permission states
    const [canModify, setCanModify] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const modifyPerm = await hasPermission('agents', 'add|edit');
                setCanModify(modifyPerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanModify(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        if (open) {
            checkPermissions();
        }
    }, [open]);

    const fetchRouterConfig = useCallback(async () => {
        if (!open) return;
        setLoading(true);
        setMessage('');
        setError('');
        
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/api/agents/router/config`, { params });
            if (response.data.success && Object.keys(response.data.data).length > 0) {
                const fetchedData = response.data.data;
                setFormData({
                    goal: fetchedData.goal || DEFAULT_ROUTER_GOAL,
                    backstory: fetchedData.backstory || DEFAULT_ROUTER_BACKSTORY,
                    task_description: fetchedData.task_description || DEFAULT_ROUTER_DESCRIPTION,
                });
            } else {
                setFormData({
                    goal: DEFAULT_ROUTER_GOAL,
                    backstory: DEFAULT_ROUTER_BACKSTORY,
                    task_description: DEFAULT_ROUTER_DESCRIPTION,
                });
            }
        } catch (err) {
            setError("Failed to fetch router agent configuration.");
        } finally {
            setLoading(false);
        }
    }, [open, paramsChatbotId]);

    useEffect(() => {
        fetchRouterConfig();
    }, [fetchRouterConfig]);

    const handleSave = async () => {
        setIsProcessing(true);
        setMessage('');
        setError('');
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.post(`/api/agents/router/config`, formData, { params });
            setMessage(response.data.message);
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save configuration.");
        } finally {
            setIsProcessing(false);
        }
    };
    
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Edit Router Agent Configuration</DialogTitle>
            <DialogContent>
                {checkingPermissions || loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : !canModify ? (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        You don't have permission to modify router agent configuration. Contact your administrator.
                    </Alert>
                ) : (
                    <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Agent Goal</Typography>
                        <TextField 
                            name="goal" 
                            multiline 
                            rows={3} 
                            fullWidth 
                            value={formData.goal} 
                            onChange={handleChange} 
                            sx={{ mb: 3 }}
                            disabled={!canModify}
                        />
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Agent Backstory</Typography>
                        <TextField 
                            name="backstory" 
                            multiline 
                            rows={3} 
                            fullWidth 
                            value={formData.backstory} 
                            onChange={handleChange} 
                            sx={{ mb: 3 }}
                            disabled={!canModify}
                        />
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Task Description
                            <span style={{ color: '#757575', marginLeft: '8px' }}>(must include '{`{user_query}`}')</span>
                        </Typography>
                        <TextField 
                            name="task_description" 
                            multiline 
                            rows={6} 
                            fullWidth 
                            value={formData.task_description} 
                            onChange={handleChange}
                            disabled={!canModify}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                {message && <Alert severity="success" sx={{ width: '100%', mr: 2 }}>{message}</Alert>}
                <Button onClick={onClose} disabled={isProcessing}>Cancel</Button>
                {canModify && (
                    <Button onClick={handleSave} variant="contained" disabled={isProcessing || loading || checkingPermissions}>
                        {isProcessing ? 'Saving...' : 'Save Configuration'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}